import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Star, Trash2, Save } from 'lucide-react';
import { useSavedSignatures } from '@/hooks/useSavedSignatures';
import type { SavedSignature, SignatureAssetType } from './types';
import { cn } from '@/lib/utils';

interface SavedSignaturesPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'signature' | 'stamp';
  onSelect: (dataUrl: string) => void;
  onCreateNew: () => void;
}

export function SavedSignaturesPicker({
  open,
  onOpenChange,
  type,
  onSelect,
  onCreateNew,
}: SavedSignaturesPickerProps) {
  const { signatures, stamps, loading, deleteSignature, setAsDefault } = useSavedSignatures();
  const [savingId, setSavingId] = useState<string | null>(null);

  const items = type === 'stamp' ? stamps : signatures;
  const title = type === 'stamp' ? 'Mes tampons' : 'Mes signatures';
  const emptyMessage = type === 'stamp' 
    ? "Aucun tampon sauvegardé" 
    : "Aucune signature sauvegardée";

  const handleSelect = (item: SavedSignature) => {
    onSelect(item.url);
    onOpenChange(false);
  };

  const handleDelete = async (item: SavedSignature) => {
    await deleteSignature(item.id, item.filePath);
  };

  const handleSetDefault = async (item: SavedSignature) => {
    await setAsDefault(item.id, item.type);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">{emptyMessage}</p>
              <Button onClick={onCreateNew}>
                <Plus className="w-4 h-4 mr-2" />
                {type === 'stamp' ? 'Ajouter un tampon' : 'Créer une signature'}
              </Button>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[300px]">
                <div className="grid grid-cols-2 gap-3">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "relative border border-border rounded-lg p-3 cursor-pointer transition-all hover:border-primary hover:shadow-md",
                        item.isDefault && "border-primary bg-primary/5"
                      )}
                      onClick={() => handleSelect(item)}
                    >
                      {item.isDefault && (
                        <Star className="absolute top-2 right-2 w-4 h-4 text-primary fill-primary" />
                      )}
                      <div className="aspect-video bg-white rounded flex items-center justify-center mb-2">
                        <img
                          src={item.url}
                          alt={item.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <p className="text-xs text-center truncate">{item.name}</p>
                      
                      {/* Actions on hover */}
                      <div className="absolute inset-0 bg-background/80 opacity-0 hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(item);
                          }}
                          title="Définir par défaut"
                        >
                          <Star className={cn("w-4 h-4", item.isDefault && "fill-primary text-primary")} />
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item);
                          }}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="mt-4 pt-4 border-t border-border">
                <Button variant="outline" className="w-full" onClick={onCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  {type === 'stamp' ? 'Nouveau tampon' : 'Nouvelle signature'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface SaveSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signatureDataUrl: string;
  type: SignatureAssetType;
  onSave: (name: string) => Promise<void>;
}

export function SaveSignatureDialog({
  open,
  onOpenChange,
  signatureDataUrl,
  type,
  onSave,
}: SaveSignatureDialogProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave(name.trim());
      setName('');
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const defaultName = type === 'stamp' ? 'Mon tampon' : 'Ma signature';
  const title = type === 'stamp' ? 'Sauvegarder le tampon' : 'Sauvegarder la signature';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="border border-border rounded-lg p-3 bg-white">
            <img
              src={signatureDataUrl}
              alt="Preview"
              className="max-w-full max-h-[100px] mx-auto object-contain"
            />
          </div>

          <div className="space-y-2">
            <Label>Nom</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={defaultName}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
